import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { searchAuthUsers, addUserWithPermissions, checkUserHasPermissions, getUserRoleByUid, updateUserPermissions, AuthUser, getAdminRoles } from '@/lib/firebase';
import { UserPlus, Search, User, Mail, Shield, Loader2 } from 'lucide-react';
import { captureHandledException } from '@/lib/sentry';

interface AdminRole {
  id: string;
  roleName: string;
  description?: string;
}

interface AddUserModalProps {
  onUserAdded: () => void;
}

export default function AddUserModal({ onUserAdded }: AddUserModalProps) {
  const [open, setOpen] = useState(false);
  const [emailQuery, setEmailQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AuthUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [existingRole, setExistingRole] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    getAdminRoles()
      .then(roles => setAdminRoles(roles.sort((a, b) => (a.roleName ?? '').localeCompare(b.roleName ?? '')) as AdminRole[]))
      .catch((error) => {
        captureHandledException(error, "users.load-admin-roles");
      });
  }, []);

  const handleSearch = async () => {
    if (!emailQuery.trim() || emailQuery.length < 3) {
      toast({
        title: "Search Query Too Short",
        description: "Please enter at least 3 characters to search for users.",
        variant: "destructive",
      });
      return;
    }

    setSearching(true);
    try {
      const results = await searchAuthUsers(emailQuery);
      setSearchResults(results);
      if (results.length === 0) {
        toast({
          title: "No Users Found",
          description: "No users found matching your search criteria.",
        });
      }
    } catch (error) {
      captureHandledException(error, "users.search");
      toast({
        title: "Search Failed",
        description: "Failed to search for users. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = async (user: AuthUser) => {
    setSelectedUser(user);
    setSearchResults([]);
    setEmailQuery('');

    const hasPermissions = await checkUserHasPermissions(user.uid);
    if (hasPermissions) {
      const permissions = await getUserRoleByUid(user.uid);
      setUserExists(true);
      setExistingRole(permissions[0] || '');
      setSelectedRole(permissions[0] || '');
    } else {
      setUserExists(false);
      setExistingRole('');
      setSelectedRole('');
    }
  };

  const handleSave = async () => {
    if (!selectedUser) {
      toast({ title: "No User Selected", description: "Please select a user.", variant: "destructive" });
      return;
    }
    if (!selectedRole) {
      toast({ title: "No Role Selected", description: "Please select a role for the user.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      if (editMode || userExists) {
        await updateUserPermissions(selectedUser.uid, [selectedRole], selectedUser.email);
        toast({ title: "User Updated Successfully", description: `${selectedUser.email} role has been updated.` });
      } else {
        await addUserWithPermissions(selectedUser.uid, [selectedRole], selectedUser.email);
        toast({ title: "User Added Successfully", description: `${selectedUser.email} has been added with the ${selectedRole} role.` });
      }

      setSelectedUser(null);
      setSelectedRole('');
      setEmailQuery('');
      setSearchResults([]);
      setUserExists(false);
      setExistingRole('');
      setEditMode(false);
      setOpen(false);
      onUserAdded();
    } catch (error) {
      captureHandledException(error, "users.save");
      toast({ title: "Failed to Save", description: "There was an error saving the user. Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setSelectedRole('');
    setEmailQuery('');
    setSearchResults([]);
    setUserExists(false);
    setExistingRole('');
    setEditMode(false);
  };

  const handleSearchAgain = () => {
    setSelectedUser(null);
    setSelectedRole('');
    setUserExists(false);
    setExistingRole('');
    setEditMode(false);
  };

  const getRoleLabel = (roleId: string) => {
    const role = adminRoles.find(r => r.id === roleId);
    return role?.roleName || roleId;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {!selectedUser ? (
            <>
              <div className="space-y-3">
                <Label htmlFor="email-search">Search by Email Address</Label>
                <div className="flex space-x-2">
                  <Input
                    id="email-search"
                    placeholder="Enter email address..."
                    value={emailQuery}
                    onChange={(e) => setEmailQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch} disabled={searching} variant="outline">
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <Label>Select User</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {searchResults.map((user) => (
                      <div
                        key={user.uid}
                        className="p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => handleSelectUser(user)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{user.displayName || user.email}</div>
                            <div className="text-sm text-slate-600 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : userExists && !editMode ? (
            <>
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-orange-800">User Already Exists</h3>
                      <p className="text-sm text-orange-700 mt-1">
                        {selectedUser?.email} already has a role assigned in the system.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{selectedUser?.displayName || selectedUser?.email}</div>
                      <div className="text-sm text-slate-600 flex items-center">
                        <Mail className="w-3 h-3 mr-1" />
                        {selectedUser?.email}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Current Role</Label>
                  <Badge variant="secondary">{getRoleLabel(existingRole)}</Badge>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button onClick={() => setEditMode(true)} className="flex-1">
                    <Shield className="w-4 h-4 mr-2" />
                    Edit Role
                  </Button>
                  <Button variant="outline" onClick={handleSearchAgain} className="flex-1">
                    <Search className="w-4 h-4 mr-2" />
                    Search Again
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Selected User */}
              <div className="space-y-3">
                <Label>{editMode ? 'Editing User' : 'Selected User'}</Label>
                <div className={`p-3 ${editMode ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 ${editMode ? 'bg-orange-100' : 'bg-blue-100'} rounded-full flex items-center justify-center`}>
                      <User className={`w-4 h-4 ${editMode ? 'text-orange-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{selectedUser.displayName || selectedUser.email}</div>
                      <div className="text-sm text-slate-600 flex items-center">
                        <Mail className="w-3 h-3 mr-1" />
                        {selectedUser.email}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleSearchAgain}>Change</Button>
                  </div>
                </div>
              </div>

              {/* Role Selection */}
              <div className="space-y-3">
                <Label>Select Role</Label>
                {adminRoles.length === 0 ? (
                  <p className="text-sm text-slate-500">Loading roles…</p>
                ) : (
                  <div className="space-y-2">
                    {adminRoles.map((role) => {
                      const isSelected = selectedRole === role.roleName;
                      return (
                        <div
                          key={role.id}
                          onClick={() => setSelectedRole(role.roleName)}
                          className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-blue-500' : 'border-slate-300'
                          }`}>
                            {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium text-sm ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                              {role.roleName}
                            </div>
                            {role.description && (
                              <p className="text-sm text-slate-500 mt-0.5">{role.description}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <Button onClick={handleSave} disabled={saving || !selectedRole} className="flex-1">
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{editMode ? 'Updating…' : 'Saving…'}</>
                  ) : editMode ? (
                    <><Shield className="w-4 h-4 mr-2" />Update Role</>
                  ) : (
                    <><UserPlus className="w-4 h-4 mr-2" />Add User</>
                  )}
                </Button>
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
